import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { AlertFactory } from '@/features/shell'
import { type Bundle } from '@/features/admin/bundle/Bundle'
import { type BundleStore } from '@/features/admin/bundle/BundleStore'
import { type RefreshBundlesFn } from '@/features/admin/bundle/BundleSearchViewModel'
import {
  formatBundleNames,
  pluralizeBundles,
  showBulkActionError,
  showBulkActionResult,
} from '@/features/admin/bundle/bulk-action-alerts'
import { type Id } from '@/infra/id'

export class BulkEditPriceDialogViewModel {
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  @observable
  accessor selectedBundles: Bundle[] = []

  /**
   * The new price entered per bundle. A bundle without an entry is left
   * unchanged; an entry of 0 sets the bundle to $0.
   */
  @observable
  accessor newPrices = new Map<Id, number>()

  constructor(
    private readonly _bundleStore: BundleStore,
    private readonly _newAlert: AlertFactory,
    private readonly _refreshBundles: RefreshBundlesFn,
  ) {}

  /**
   * Archived bundles can't be repriced, so they never appear in the editor.
   */
  @computed
  get eligibleBundles() {
    return this.selectedBundles.filter((bundle) => !bundle.archived)
  }

  @computed
  get archivedCount() {
    return this.selectedBundles.length - this.eligibleBundles.length
  }

  @computed
  get hasEligibleBundles() {
    return this.eligibleBundles.length > 0
  }

  @computed
  get hasArchivedBundles() {
    return this.archivedCount > 0
  }

  /**
   * The bundles the user gave a new price. Blank rows are skipped; a $0 entry
   * is a real edit.
   */
  @computed
  get edits() {
    return this.eligibleBundles.flatMap((bundle) => {
      const price = this.newPrices.get(bundle.id)
      return price === undefined ? [] : [{ bundle, price }]
    })
  }

  @computed
  get canApply() {
    return this.edits.length > 0
  }

  priceFor(bundleId: Id): number | null {
    return this.newPrices.get(bundleId) ?? null
  }

  @action.bound
  setPrice(bundleId: Id, price: number | null) {
    if (price === null) {
      this.newPrices.delete(bundleId)
    } else {
      this.newPrices.set(bundleId, price)
    }
  }

  @action.bound
  open(bundles: Bundle[]) {
    this.reset()
    this.selectedBundles = bundles

    this.modal.show()
  }

  @action.bound
  reset() {
    this.selectedBundles = []
    this.newPrices.clear()

    this.submit.reset()
  }

  submit = task.resolved(async () => {
    const edits = this.edits

    if (edits.length === 0) {
      return
    }

    try {
      const result = await this._bundleStore.bulkEditPrices({
        workspaceId: edits[0].bundle.workspace.id,
        priceUpdates: edits.map((edit) => ({
          bundleId: edit.bundle.id,
          price: edit.price,
        })),
      })

      const skippedIds = new Set<Id>([
        ...result.notFoundIds,
        ...result.archivedIds,
      ])

      showBulkActionResult(
        this._newAlert,
        edits.map((edit) => edit.bundle),
        skippedIds,
        {
          success: (count) =>
            `Updated the price of ${count} ${pluralizeBundles(count)}.`,
          partialTitle: 'Some bundles were skipped',
          partial: (count, skipped) =>
            `Updated ${count} of ${count + skipped.length} bundles. Skipped: ${formatBundleNames(skipped)}.`,
        },
      )
    } catch (e) {
      showBulkActionError(
        this._newAlert,
        e,
        'You do not have permission to edit prices in this workspace.',
        'There was an error updating the prices.',
      )
      this.modal.close()
      return
    }

    await this._refreshBundles(edits[0].bundle.workspace)

    this.modal.close()
  })
}
